<script setup lang="ts">
import DisplayDeliveryPlanInformation from '@/components/DailyReportEdit/AlterLeaving/DisplayDeliveryPlanInformation.vue';
import getDeliveryPlanVehicleInformation from '@/types/responses/getDeliveryPlanVehicleInformation';
import RegisterModal from '@/components/DailyReportEdit/AlterLeaving/RegisterModal.vue';
import DatePicker from '@/components/DailyReportEdit/AlterLeaving/DatePicker.vue';
import CompleteModal from '@/components/DailyReportEdit/Split/CompleteModal.vue';
import ValidationErrors from '@/components/Common/ValidationErrors.vue';
import { formatDateTimeWithSeconds } from '@/services/functions';
import { useAuth } from '@/services/user/Auth0UserProvider';
import Api from '@/services/api/ApiServiceFabrick';
import { ref, Ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import i18n from '@/lang/i18n';

const props = defineProps<{
  showFlash: (message: string, type: string, fadeout: boolean) => void
  deliveryPlan: getDeliveryPlanVehicleInformation
}>();

const { loginWithRedirect } = useAuth();
const router = useRouter();
const isProcessing: Ref<boolean> = ref(false);
const errorMessages = ref<{
  returning_time: string[]
  delivery_plan_id: string[]
}>({
  returning_time: [],
  delivery_plan_id: []
});

const returningDateTime: Ref<Date | null> = ref(props.deliveryPlan.returning_warehouse_date_time ? new Date(props.deliveryPlan.returning_warehouse_date_time) : null);
const isLoading: Ref<boolean> = ref(false);
const isOpenDialog: Ref<boolean> = ref(false);
const isDisplayCompleteModal: Ref<boolean> = ref(false);

const isActiveExecBtn = computed(() => {
  return returningDateTime.value?.getTime() != new Date(props.deliveryPlan.returning_warehouse_date_time).getTime();
})

const execBtnClass = computed(() => {
  return {
    'exec-btn-disabled': isProcessing.value || !isActiveExecBtn.value
  };
});

function moveDailyReportDisplay() {
  router.push({ name: "DailyReport" })
}

function execAlterReturning() {
  isLoading.value = true;
  errorMessages.value = {
    returning_time: [],
    delivery_plan_id: []
  };

  Api().alterReturning(props.deliveryPlan.delivery_plan_id, makeRequest()).then((data) => {
    if (data.status) {
      return isDisplayCompleteModal.value = true;
    }
    props.showFlash(i18n.global.t('pages.dailyReports.splitForm.failedMessage'), 'error', true);
  }).catch((error) => {
    errorHandler(error);
  }).finally(() => {
    isLoading.value = false;
  });
}

function makeRequest() {
  return {
    returning_time: returningDateTime.value ? formatDateTimeWithSeconds(returningDateTime.value) : ''
  }
}

function errorHandler(error: any) {
  const res = error.response
  switch (res.status) {
    case 401:
      loginWithRedirect({ appState: { targetUrl: location.pathname } })
      break
    case 403:
      location.reload()
      break
    case 422:
      errorMessages.value = res.data.errors
      if (errorMessages.value['delivery_plan_id']?.length) {
        isProcessing.value = true;
      }
  }
}
</script>
<template>
  <div class="d-flex" style="min-height: 45vw">
    <div class="content p-4 w-50 mx-auto">
      <v-card class="my-4 pa-4">
        <h4 class="card-title mt-0 mb-4">
          {{ i18n.global.t('pages.dailyReports.alterLeavingOrReturning.returningTitle') }}
        </h4>
        <v-row class="mt-2 mb-4 ml-12">
          <DisplayDeliveryPlanInformation
            :deliveryPlan="props.deliveryPlan"
          />
        </v-row>
        <v-row class="text-center">
          <v-col
            cols="4"
            class="ml-12"
          >
            <DatePicker
              v-model="returningDateTime"
              :deliveryPlan="props.deliveryPlan"
              :label="i18n.global.t('pages.dailyReports.alterLeavingOrReturning.returningDateTime')"
              :minDate="props.deliveryPlan.leaving_warehouse_date_time"
              :errorMessages="errorMessages.returning_time"
            />
          </v-col>
        </v-row>
        <div
          class="d-flex justify-content-end m-3"
        >
          <v-btn
            class="ma-2 reset-btn"
            :disabled="isLoading"
            @click="moveDailyReportDisplay"
          >
            {{ i18n.global.t('common.cancelBtn') }}
          </v-btn>
          <v-btn
            class="ma-2 exec-btn"
            :loading="isLoading"
            :disabled="isProcessing || !isActiveExecBtn"
            :class="execBtnClass"
            @click="isOpenDialog = true"
          >
            {{ i18n.global.t('common.confirmBtn') }}
          </v-btn>
        </div>
        <ValidationErrors
          class="d-flex justify-content-end"
          :messages="errorMessages.delivery_plan_id"
        >
        </ValidationErrors>
      </v-card>
    </div>
  </div>
  <RegisterModal
    :title="i18n.global.t('pages.dailyReports.alterLeavingOrReturning.confirmTitle')"
    v-model="isOpenDialog"
    :message="i18n.global.t('pages.dailyReports.alterLeavingOrReturning.returningMessage')"
    :changeValues="[
      props.deliveryPlan.returning_warehouse_date_time,
      formatDateTimeWithSeconds(returningDateTime ?? new Date(props.deliveryPlan.returning_warehouse_date_time))
    ]"
    :showFlash="props.showFlash"
    :execFunction="execAlterReturning"
  />
  <CompleteModal
    v-model="isDisplayCompleteModal"
    :messages="[
      i18n.global.t('pages.dailyReports.alterReturningCompleteModal.messageRow1'),
      i18n.global.t('pages.dailyReports.alterReturningCompleteModal.messageRow2')
    ]"
    :moveDailyReportDisplay="moveDailyReportDisplay"
  />
</template>
<style scoped>
.exec-btn-disabled {
  background-color: #B3B8BB;
}
</style>
